import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Gavel, Search, MapPin, Euro, Calendar, FileText,
  Sparkles, Filter, Globe, Building2, ExternalLink, Eye, Loader2, RefreshCw,
} from 'lucide-react';
import {
  listTenders, getTenderStats, deepSearchTenders, getVectorIndexStats,
  listTenderIndustries, searchOpportunitiesNow, pullSphinxTenders,
} from '../lib/api';
import cpvData from '../data/cpv_pl.json';
import InvestmentMap from '../components/tenders/InvestmentMap';
import { markMissionComplete, MISSIONS } from '../lib/tutorialActions';
import Tour, { shouldShowTour } from '../components/tutorial/Tour';
import PlanLock from '../components/common/PlanLock';
import TermTooltip from '../components/common/TermTooltip';

const CPV_NAMES = cpvData.codes || {};

function cpvName(code) {
  if (!code) return '';
  // Try exact match, then 8-digit prefix
  if (CPV_NAMES[code]) return CPV_NAMES[code];
  const eightDigit = String(code).split('-')[0];
  for (const key of Object.keys(CPV_NAMES)) {
    if (key.split('-')[0] === eightDigit) return CPV_NAMES[key];
  }
  return '';
}

function matchScoreBadge(score, label) {
  if (score == null) return null;
  const cls =
    label === 'high' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
    label === 'medium' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
    'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
  return { cls, score };
}

const SOURCE_LABELS = { bzp: 'BZP Polska', ted: 'TED Europa', auction: 'Licytacje' };
const SOURCE_COLORS = {
  bzp: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  ted: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  auction: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

const VOIVODESHIPS = [
  'mazowieckie', 'śląskie', 'wielkopolskie', 'małopolskie',
  'dolnośląskie', 'lubelskie', 'łódzkie', 'pomorskie',
  'zachodniopomorskie', 'podkarpackie', 'świętokrzyskie', 'warmińsko-mazurskie',
  'podlaskie', 'kujawsko-pomorskie', 'lubuskie', 'opolskie',
];

function fmtMoney(v, currency = 'PLN') {
  if (!v || v === 0) return '—';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' ' + currency;
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s.slice(0, 10);
  }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const now = new Date();
    const d = new Date(dateStr);
    const diff = Math.floor((d - now) / (1000 * 60 * 60 * 24));
    return diff;
  } catch {
    return null;
  }
}

// ===== Filters Sidebar =====
function FiltersSidebar({ filters, setFilters, stats, industries }) {
  const byRegion = stats?.by_region || [];
  const topCpv = stats?.top_cpv || [];

  return (
    <div className="space-y-4">
      {/* Branża — match-score driver */}
      {industries && industries.length > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              {industries.length <= 1 ? 'Twoja branża' : 'Branża (match score)'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {industries.length <= 1 ? (
              // Branża ZAMKNIĘTA na koncie — etykieta, nie dropdown. Zmienia/dodaje tylko
              // właściciel (panel właściciela). Klient nie wybiera cudzych kierunków.
              <div className="px-3 py-2 rounded border border-primary/30 bg-primary/10 text-sm font-medium text-foreground flex items-center gap-2">
                <span>{industries[0]?.name || filters.industry}</span>
                <span className="text-[10px] text-muted-foreground font-normal ml-auto">kierunek konta</span>
              </div>
            ) : (
              <Select
                value={filters.industry || industries[0]?.key}
                onValueChange={v => setFilters({ ...filters, industry: v, page: 1 })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {industries.map(ind => (
                    <SelectItem key={ind.key} value={ind.key}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="text-xs text-muted-foreground">Min score:</div>
            <div className="flex gap-1 flex-wrap">
              {[0, 40, 60, 80].map(s => (
                <button
                  key={s}
                  onClick={() => setFilters({ ...filters, min_score: s || undefined, page: 1 })}
                  className={`text-xs px-2 py-1 rounded border ${
                    (filters.min_score || 0) === s
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'border-muted hover:bg-muted'
                  }`}
                >
                  {s === 0 ? 'wszystkie' : `≥${s}`}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Źródło */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Źródło
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {['', 'bzp', 'ted', 'auction'].map(src => {
            const isActive = (filters.source || '') === src;
            const srcStats = stats?.by_source?.find(s => s._id === src);
            return (
              <button
                key={src || 'all'}
                onClick={() => setFilters({ ...filters, source: src || undefined, page: 1 })}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition ${
                  isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-muted-foreground'
                }`}
              >
                <span>{src ? SOURCE_LABELS[src] : 'Wszystkie'}</span>
                {srcStats && <span className="text-xs">{srcStats.count}</span>}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Województwo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Województwo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={filters.region || 'all'}
            onValueChange={v => setFilters({ ...filters, region: v === 'all' ? undefined : v, page: 1 })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie</SelectItem>
              {VOIVODESHIPS.map(v => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
              {byRegion.filter(r => r._id && !VOIVODESHIPS.includes(r._id)).map(r => (
                <SelectItem key={r._id} value={r._id}>{r._id} ({r.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* CPV */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Kod <TermTooltip term="CPV" showIcon>CPV</TermTooltip>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="np. 45233100"
            value={filters.cpv || ''}
            onChange={e => setFilters({ ...filters, cpv: e.target.value || undefined, page: 1 })}
          />
          {topCpv.slice(0, 6).map(c => (
            <button
              key={c._id}
              onClick={() => setFilters({ ...filters, cpv: c._id, page: 1 })}
              className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/70 mr-1 mb-1"
            >
              {c._id} ({c.count})
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Min wartość */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Min. wartość PLN
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="number"
            placeholder="0"
            value={filters.min_value || ''}
            onChange={e => setFilters({
              ...filters,
              min_value: e.target.value ? Number(e.target.value) : undefined,
              page: 1,
            })}
          />
        </CardContent>
      </Card>

      {/* Deadline po */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Deadline po
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="date"
            value={filters.deadline_after || ''}
            onChange={e => setFilters({
              ...filters,
              deadline_after: e.target.value || undefined,
              page: 1,
            })}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ===== Deep Search =====
function DeepSearchBar({ onResults, loading, setLoading }) {
  const [query, setQuery] = useState('');
  const [vectorStats, setVectorStats] = useState(null);

  useEffect(() => {
    getVectorIndexStats().then(setVectorStats).catch(() => {});
  }, []);

  // 2026-05-27 fix #2: prawdziwy podwójny search.
  // Plan dotychczasowy: zawsze deepSearch z top_k=20 → user widzi "20" niezależnie od query.
  // Teraz:
  //   1. Najpierw szybki listTenders({q}) — zwraca PRAWDZIWY total z DB
  //   2. Jeśli total = 0 → fallback do deep_search (RAG semantyczny)
  //   3. Komunikat informuje user'a co dostał (regex vs semantic + ile naprawdę)
  const handleSearch = async () => {
    const qTrim = query.trim();
    if (!qTrim) {
      onResults(null);
      return;
    }
    setLoading(true);
    try {
      // 1. Standard text search — używa $text/regex na backendzie (tenders_router.py)
      const textRes = await listTenders({ q: qTrim, page: 1, page_size: 100 });
      if (textRes && textRes.total > 0) {
        // Wyniki tekstowe — zwracamy jako quasi-deep results dla unified UI
        onResults({
          total: textRes.total,
          search_mode: 'text',
          results: (textRes.items || []).map((t) => ({
            tender: t,
            chunks: [],
            score: 1.0,
          })),
        });
        toast.success(
          `Znaleziono ${textRes.total} przetargów zawierających "${qTrim}"` +
          (textRes.total > 100 ? ` (pokazuję top 100)` : '')
        );
        return;
      }
      // 2. Fallback do semantycznego deep search jeśli text search nic nie znalazł
      const deepRes = await deepSearchTenders({ query: qTrim, top_k: 50 });
      onResults({
        ...deepRes,
        search_mode: 'semantic',
      });
      if (deepRes.total > 0) {
        toast.info(
          `Brak dokładnych dopasowań. Top ${deepRes.total} podobnych semantycznie (AI vector search).`
        );
      } else {
        toast.warning(`Brak wyników dla "${qTrim}" — ani tekstowo ani semantycznie.`);
      }
    } catch (e) {
      toast.error('Szukanie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
      <CardContent className="pt-4 pb-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-medium text-foreground">Deep Search AI</span>
          <span>— przeszukuje wnętrze plików SWZ semantycznie</span>
          {vectorStats && (
            <Badge variant="outline" className="ml-auto">
              {vectorStats.total_chunks} chunków · {vectorStats.unique_tenders} przetargów
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="np. 'modernizacja kotłowni', 'dostawa mebli', 'roboty stolarskie'"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="flex-1"
          />
          <Button onClick={handleSearch} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Szukaj</span>
          </Button>
          {query && (
            <Button variant="outline" onClick={() => { setQuery(''); onResults(null); }}>
              Wyczyść
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ===== Main =====
// T61 — Etap 1 redesignu Tenders: czytelny, persona-aware przewodnik flow.
// Adresuje zgłoszenie Izy ("nieintuicyjny, nie wiadomo o co chodzi, tajemnicze skróty").
function _readPersona() {
  try { return JSON.parse(localStorage.getItem('user') || '{}')?.preferences?.persona || 'seller_led'; }
  catch { return 'seller_led'; }
}

function TenderFlowGuide() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('tenders_guide_collapsed') !== '1'; } catch { return true; }
  });
  const persona = _readPersona();
  const toggle = () => {
    const next = !open; setOpen(next);
    try { localStorage.setItem('tenders_guide_collapsed', next ? '0' : '1'); } catch {}
  };

  if (persona === 'designer') {
    return (
      <div className="glass-card rounded-xl p-3 border border-fuchsia-500/20 text-sm">
        <div className="flex items-start gap-2">
          <span className="text-fuchsia-300 font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Dla projektanta:</span>
          <span className="text-muted-foreground">
            Ta zakładka to <b>przetargi publiczne</b> (BZP/TED) — przydatna gdy startujesz w zamówieniach publicznych.
            Jeśli szukasz <b>inwestycji i kontaktów do współpracy</b>, użyj zakładek <b>Inwestycje</b> i <b>Projektanci wnętrz</b>.
          </span>
        </div>
      </div>
    );
  }

  const steps = [
    { n: 1, t: 'Agent skanuje', d: 'MASKIL pobiera przetargi z BZP/TED pasujące do Twojej branży (automatycznie, 24/7).' },
    { n: 2, t: 'Analizujesz dopasowanie', d: 'Match score pokazuje które warte uwagi. Klik „Analizuj" → AI ocenia SWZ i szanse.' },
    { n: 3, t: 'Profit Room liczy marżę', d: 'BoQ + zapytania (RFQ) do dostawców → ile zarobisz. To Ty kupujesz produkty/materiały, by zrealizować zamówienie.' },
    { n: 4, t: 'Składasz ofertę', d: 'Generujesz dokumenty (JEDZ/oferta/PDF) i startujesz w przetargu.' },
  ];
  return (
    <div className="glass-card rounded-xl p-3">
      <button onClick={toggle} className="w-full flex items-center justify-between text-sm font-medium">
        <span className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          ℹ️ Jak to działa (od przetargu do wygranej)
        </span>
        <span className="text-xs text-muted-foreground">{open ? 'zwiń ▲' : 'rozwiń ▼'}</span>
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
          {steps.map(s => (
            <div key={s.n} className="rounded-lg p-2.5 bg-white/5">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">{s.n}</span>
                <span className="text-sm font-medium">{s.t}</span>
              </div>
              <div className="text-[11px] text-muted-foreground leading-snug">{s.d}</div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="text-[11px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
          Skróty: <b>SWZ</b> = dokumentacja przetargu · <b>RFQ</b> = zapytanie ofertowe do dostawców ·
          <b> BoQ</b> = przedmiar/wykaz pozycji · <b>JEDZ</b> = oświadczenie wykonawcy. Pełny słownik w zakładce „Słownik".
        </div>
      )}
    </div>
  );
}

export default function Tenders({ lang, tenantId }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    page: 1,
    page_size: 20,
    sort: 'match',           // default: match-score sort (most relevant first)
    industry: 'oswietlenie',
  });
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deepSearchResults, setDeepSearchResults] = useState(null);
  const [deepSearchLoading, setDeepSearchLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');  // 'table' | 'map'
  const [mapItems, setMapItems] = useState([]);  // Full set for map view (ignores pagination)
  const [mapSource, setMapSource] = useState('all');  // zgł. 68ab58b7 — wybór źródła na mapie
  const [mapLoading, setMapLoading] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Auto-launch tour on first visit (after data settles)
  useEffect(() => {
    const tid = setTimeout(() => {
      if (shouldShowTour('tenders-overview')) setShowTour(true);
    }, 1400);
    return () => clearTimeout(tid);
  }, []);

  // Load industries list once. Branża to ZAMKNIĘTY atrybut konta — backend zwraca tylko
  // dozwolone kierunki tenanta + default=primary. Ustaw industry na primary (nie hardcode
  // 'oswietlenie'), żeby klient meblowy/HVAC widział od razu swoją branżę.
  useEffect(() => {
    listTenderIndustries()
      .then(r => {
        setIndustries(r.industries || []);
        if (r.default) setFilters(f => ({ ...f, industry: r.default }));
      })
      .catch(() => {});
    // Tutorial: mission 1 — browsed tenders list
    markMissionComplete(MISSIONS.BROWSE_TENDERS);
  }, []);

  // Fetch full geocoded set when entering map mode
  useEffect(() => {
    if (viewMode !== 'map') return;
    let cancelled = false;
    setMapLoading(true);
    listTenders({ ...filters, page: 1, page_size: 1000 })
      .then(r => { if (!cancelled) setMapItems(r.items || []); })
      .catch(() => { if (!cancelled) setMapItems([]); })
      .finally(() => { if (!cancelled) setMapLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tenders, st] = await Promise.all([
        listTenders(filters),
        getTenderStats(),
      ]);
      setData(tenders);
      setStats(st);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Ręczny skan/odświeżenie przetargów na żądanie:
  //  (1) własny skan BZP/TED (OpportunityGhost — pobiera + zapisuje nowe z dedupem)
  //  (2) pull z mostu SPHINX (gdy strona SPHINX dowozi notices; bezpieczny no-op gdy 0)
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    const tId = toast.loading('Skanuję BZP/TED + SPHINX — to może potrwać do ~minuty...');
    try {
      const [own, bridge] = await Promise.allSettled([
        searchOpportunitiesNow({}),
        pullSphinxTenders({}),
      ]);
      const ghostN = own.status === 'fulfilled' ? (own.value?.signals_found ?? 0) : 0;
      const sphinxNew = bridge.status === 'fulfilled' ? (bridge.value?.inserted ?? 0) : 0;
      await fetchData();
      toast.success(
        `Odświeżono. Skan BZP/TED: ${ghostN} sygnałów · most SPHINX: ${sphinxNew} nowych.`,
        { id: tId }
      );
    } catch (e) {
      toast.error('Odświeżanie nie powiodło się: ' + (e.response?.data?.detail || e.message), { id: tId });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 1;
  const items = deepSearchResults ? deepSearchResults.results.map(r => ({
    ...r.tender,
    _deep_chunks: r.chunks,
    _deep_score: r.score,
  })) : (data?.items || []);

  // Klikalne nagłówki kolumn (zgł. „sortowanie zawartości okna jak w Eksploratorze Windows").
  // Klik = sortuj po kolumnie; ponowny klik = odwróć kierunek. Match/Trafność zostają w dropdownie.
  const COL_NATURAL_DIR = { title: 'asc', source: 'asc', region: 'asc', value: 'desc', deadline: 'asc' };
  const toggleSort = (col) => {
    if (deepSearchResults) return;  // sort kolumnowy nieaktywny w trybie Deep Search
    setFilters(f => f.sort === col
      ? { ...f, sort_dir: f.sort_dir === 'asc' ? 'desc' : 'asc', page: 1 }
      : { ...f, sort: col, sort_dir: COL_NATURAL_DIR[col] || 'asc', page: 1 });
  };
  const SortableHead = ({ col, children, className }) => {
    const active = filters.sort === col;
    const arrow = active ? (filters.sort_dir === 'asc' ? '▲' : '▼') : '';
    return (
      <TableHead
        className={`${className || ''} cursor-pointer select-none hover:text-primary transition-colors`}
        onClick={() => toggleSort(col)}
        title="Kliknij, aby sortować po tej kolumnie"
      >
        {children}{arrow && <span className="ml-1 text-primary">{arrow}</span>}
      </TableHead>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gavel className="w-6 h-6 text-primary" />
            Przetargi publiczne
          </h1>
          <p className="text-sm text-muted-foreground">
            BZP + TED + licytacje komornicze · analiza AI Consilium · Deep Search w SWZ
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <Badge variant="outline" className="text-base py-1 px-3">
            {stats?.total || 0} przetargów
          </Badge>
          <Badge variant="outline" className="text-base py-1 px-3">
            <FileText className="w-3.5 h-3.5 mr-1" />
            {stats?.attachments_total || 0} SWZ pobranych
          </Badge>
          <Button onClick={handleRefresh} disabled={refreshing} variant="outline" className="gap-1.5"
                  title="Skanuj BZP/TED teraz + pobierz nowe z SPHINX">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Skanuję...' : 'Odśwież przetargi'}
          </Button>
        </div>
      </div>

      {/* T61 — przewodnik flow (persona-aware) */}
      <TenderFlowGuide />

      {/* Deep search (PRO feature — backend zwraca 402 dla starter) */}
      <div data-tour="tenders-deep-search" className="relative">
        <DeepSearchBar
          onResults={setDeepSearchResults}
          loading={deepSearchLoading}
          setLoading={setDeepSearchLoading}
        />
        <div className="absolute top-2 right-2">
          <PlanLock feature="tenders_deep_search" requiredPlan="pro" inline lang={lang}>
            <span />
          </PlanLock>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Filters */}
        <aside className="col-span-3" data-tour="tenders-filters">
          <FiltersSidebar filters={filters} setFilters={setFilters} stats={stats} industries={industries} />
        </aside>

        {/* Table */}
        <div className="col-span-9 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {deepSearchResults ? (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  Wyniki Deep Search: {deepSearchResults.total}
                </span>
              ) : (
                data && <span>Łącznie: {data.total}, strona {data.page}/{totalPages}</span>
              )}
            </div>
            <div className="flex gap-2" data-tour="tenders-view-sort">
              <div className="flex border rounded overflow-hidden">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 text-xs ${viewMode === 'table' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                >
                  📋 Tabela
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-1 text-xs ${viewMode === 'map' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-muted'} relative flex items-center gap-1`}
                  title="Mapa inwestycji (PRO+)"
                >
                  🗺️ Mapa
                  <PlanLock feature="tenders_investment_map" requiredPlan="pro" inline lang={lang}>
                    <span />
                  </PlanLock>
                </button>
              </div>
              <Select
                value={filters.sort}
                onValueChange={v => setFilters({ ...filters, sort: v })}
                disabled={!!deepSearchResults}
              >
                <SelectTrigger className="w-48"><SelectValue placeholder="↕ Sort: kolumna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="match">Match score (najlepsze)</SelectItem>
                  <SelectItem value="published">Najnowsze</SelectItem>
                  <SelectItem value="deadline">Termin rosnąco</SelectItem>
                  <SelectItem value="value">Wartość malejąco</SelectItem>
                  <SelectItem value="relevance">Trafność</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {viewMode === 'map' && (() => {
            // zgł. 68ab58b7 — wybór co pokazać na mapie + uczciwy licznik bez lokalizacji.
            const bySrc = mapSource === 'all' ? mapItems : mapItems.filter(i => i.source === mapSource);
            const plottable = bySrc.filter(i => i.lat != null && i.lon != null);
            const SRC_LABELS = { all: 'Wszystkie źródła', bzp: 'BZP', ted: 'TED', auction: 'Licytacje' };
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <Select value={mapSource} onValueChange={setMapSource}>
                    <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SRC_LABELS).map(([k, lbl]) => {
                        const n = k === 'all' ? mapItems.length : mapItems.filter(i => i.source === k).length;
                        const g = (k === 'all' ? mapItems : mapItems.filter(i => i.source === k))
                          .filter(i => i.lat != null && i.lon != null).length;
                        return <SelectItem key={k} value={k}>{lbl} ({g}/{n} na mapie)</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  {bySrc.length > plottable.length && (
                    <span className="text-[11px] text-amber-300">
                      ⚠ {bySrc.length - plottable.length} bez lokalizacji (geokodowanie w toku — BZP/TED często nie podają adresu)
                    </span>
                  )}
                </div>
                <InvestmentMap
                  items={bySrc}
                  onMarkerClick={(sourceId) => navigate(`/tenders/${encodeURIComponent(sourceId)}`)}
                  onBackfillDone={() => {
                    listTenders({ ...filters, page: 1, page_size: 1000 })
                      .then(r => setMapItems(r.items || []))
                      .catch(() => {});
                  }}
                  height="600px"
                />
              </div>
            );
          })()}
          {viewMode === 'map' && mapLoading && (
            <div className="text-xs text-muted-foreground text-center py-2">
              Ładuję pełen zbiór dla mapy (max 1000)...
            </div>
          )}
          {viewMode === 'table' && (
          <Card data-tour="tenders-table" className="glass-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead col="title" className="w-[44%]">Tytuł / Zamawiający</SortableHead>
                  <SortableHead col="source">Źródło</SortableHead>
                  <SortableHead col="region">Region</SortableHead>
                  <TableHead>CPV</TableHead>
                  <SortableHead col="value">Wartość</SortableHead>
                  <SortableHead col="deadline">Termin</SortableHead>
                  <TableHead>SWZ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={'s' + i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      Brak przetargów dla tych filtrów
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.map((it, idx) => {
                  const days = daysUntil(it.deadline);
                  const deadlineColor =
                    days == null ? 'text-muted-foreground'
                    : days < 0 ? 'text-red-400'
                    : days <= 3 ? 'text-red-500'
                    : days <= 7 ? 'text-amber-400'
                    : days <= 30 ? 'text-foreground'
                    : 'text-muted-foreground';
                  return (
                    <TableRow
                      key={it.source_id + idx}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => navigate(`/tenders/${encodeURIComponent(it.source_id)}`)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-start gap-2">
                          {it.match_score != null && (() => {
                            const b = matchScoreBadge(it.match_score, it.match_label);
                            return b ? (
                              <span
                                className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border ${b.cls}`}
                                title={`Dopasowanie do branży: ${it.match_score}/100 (${it.match_label}). Słowa: ${(it.matched_keywords || []).join(', ') || '—'}. Reaguje na wybór branży (prawy badge 🏆 = szansa wygranej, nie reaguje).`}
                              >
                                {b.score}
                              </span>
                            ) : null;
                          })()}
                          {/* FIX #3 (2026-05-11): Win-rate badge w liście (embed z tender_win_predictions) */}
                          {it.win_rate?.score != null && (
                            <span
                              className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                                it.win_rate.score >= 60
                                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                  : it.win_rate.score >= 30
                                  ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                                  : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40'
                              }`}
                              title={`Szansa wygranej przetargu: ${it.win_rate.score}% (${it.win_rate.category || ''}) · ${it.win_rate.recommendation || ''} — NIEZALEŻNE od wybranej branży (liczone raz z SWZ). To NIE jest dopasowanie do branży (lewy badge).`}
                            >
                              🏆 {it.win_rate.score}%
                            </span>
                          )}
                          <div className="text-sm line-clamp-2 flex-1">
                            {it.title || '—'}
                            {/* Licytacje komornicze mają generyczny tytuł ("Licytacja nieruchomości
                                nieruchomość") → 88 RÓŻNYCH licytacji w 78 miastach wyglądało jak
                                duplikaty. Miasto w tytule = każdy wiersz rozróżnialny. */}
                            {it.source === 'auction' && it.city && (
                              <span className="text-muted-foreground font-normal"> — {it.city}</span>
                            )}
                          </div>
                        </div>
                        {it.description && (
                          <div className="text-xs text-muted-foreground/90 line-clamp-2 mt-1 leading-snug">
                            {it.description}
                          </div>
                        )}
                        {it.contracting_authority && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Building2 className="w-3 h-3" />
                            {Array.isArray(it.contracting_authority)
                              ? it.contracting_authority[0]
                              : it.contracting_authority}
                          </div>
                        )}
                        {it._deep_chunks && it._deep_chunks.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {it._deep_chunks.slice(0, 3).map((ch, idx) => (
                              <div key={idx} className="text-[11px] text-primary/80 flex items-start gap-1.5">
                                {ch.pdf_link && (
                                  <a
                                    href={ch.pdf_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary shrink-0 font-medium"
                                    title={ch.attachment_filename || 'Otwórz PDF'}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    s.{ch.page_no}
                                  </a>
                                )}
                                <span className="italic line-clamp-2">
                                  …{(ch.excerpt || ch.text || '').slice(0, 200)}…
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={SOURCE_COLORS[it.source] || ''}>
                          {SOURCE_LABELS[it.source] || it.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{it.region || '—'}</TableCell>
                      <TableCell className="text-xs">
                        {(() => {
                          const codes = (it.cpv_codes || []).slice(0, 2);
                          if (codes.length === 0) return '—';
                          const firstName = cpvName(codes[0]);
                          return (
                            <div title={codes.map(c => `${c}: ${cpvName(c) || '?'}`).join('\n')}>
                              <div className="font-mono">{codes.join(', ')}</div>
                              {firstName && (
                                <div className="text-muted-foreground/80 line-clamp-1">{firstName}</div>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-xs">{fmtMoney(it.estimated_value, it.currency)}</TableCell>
                      <TableCell className={`text-xs ${deadlineColor}`}>
                        {fmtDate(it.deadline)}
                        {days != null && days >= 0 && days <= 30 && (
                          <div className="text-[10px]">{days}d</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {it.attachment_count > 0 ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                            {it.attachment_count}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          )}

          {/* Pagination */}
          {viewMode === 'table' && !deepSearchResults && data && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={data.page <= 1}
                onClick={() => setFilters({ ...filters, page: Math.max(1, data.page - 1) })}
              >
                ← Poprzednia
              </Button>
              <span className="text-sm text-muted-foreground">
                {data.page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={data.page >= totalPages}
                onClick={() => setFilters({ ...filters, page: Math.min(totalPages, data.page + 1) })}
              >
                Następna →
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* In-context tour */}
      <Tour
        tourId="tenders-overview"
        lang={lang}
        active={showTour}
        onComplete={() => setShowTour(false)}
        onSkip={() => setShowTour(false)}
        steps={[
          {
            target: '[data-tour="tenders-filters"]',
            title: lang === 'pl' ? 'Filtry i match score' : 'Filters & match score',
            content: lang === 'pl'
              ? 'Wybierz branżę aby ADAM uczył się Twojego profilu i podbijał trafność. Im więcej oznaczasz „pomijam"/„chcę widzieć", tym celniejsze wyniki.'
              : 'Pick an industry — ADAM learns your profile and boosts relevance. Each "skip" or "want" you mark sharpens future matches.',
            placement: 'right',
          },
          {
            target: '[data-tour="tenders-deep-search"]',
            title: lang === 'pl' ? 'Deep Search w SWZ' : 'Deep Search in SWZ',
            content: lang === 'pl'
              ? 'Szukaj w treści załączników SWZ semantycznie — np. „dostawa foteli biurowych" znajdzie nawet jeśli tytuł brzmi „wyposażenie biura".'
              : 'Search semantically inside SWZ attachments — e.g. "office chairs" still finds tenders titled "office furnishing".',
            placement: 'bottom',
          },
          {
            target: '[data-tour="tenders-view-sort"]',
            title: lang === 'pl' ? 'Widok tabeli i mapy' : 'Table & map view',
            content: lang === 'pl'
              ? 'Przełącz na mapę aby zobaczyć przetargi geograficznie. Sortuj po Match Score aby najlepsze szanse były na górze.'
              : 'Switch to map view to see tenders geographically. Sort by Match Score to keep the best leads on top.',
            placement: 'left',
          },
          {
            target: '[data-tour="tenders-table"]',
            title: lang === 'pl' ? 'Tabela przetargów' : 'Tenders table',
            content: lang === 'pl'
              ? 'Kliknij wiersz aby przejść do szczegółów — tam analiza AI Consilium, BoQ, win-rate i akcje (Profit Room, JEDZ).'
              : 'Click a row to open details — there you\'ll find AI analysis, BoQ extraction, win-rate, and actions (Profit Room, JEDZ).',
            placement: 'top',
          },
        ]}
      />
    </div>
  );
}
